import { RefreshCw } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import {
    IncludeExcludeFilterMenu,
    type IncludeOnlyFilterMenuCategory,
} from '@apps/web/components/filters/include_exclude_filter_menu';
import { Button } from '@apps/web/components/ui/button';
import { Checkbox } from '@apps/web/components/ui/checkbox';
import { SearchInput } from '@apps/web/components/ui/search_input';
import {
    type CoverageFilters,
    DAY_PARTS,
    type DayPartKey,
    EMPLOYMENT_TYPE_LABELS,
} from '@apps/web/features/coverage/model/coverage_types';
import { type FacetOptions } from '@apps/web/features/coverage/model/filter_model';
import { type CoverageSearchUpdate } from '@apps/web/features/coverage/model/use_coverage_dashboard';
import { useDebouncedValue } from '@apps/web/lib/use_debounced_value';

const SEARCH_DEBOUNCE_MS = 300;

export function CoverageFilterBar({
    filters,
    searchControl,
    mapControls,
    facetOptions,
    dayLabels,
    filtersActive,
    clearEpoch,
    includeInactive,
    includeDoNotContact,
    onIncludeInactiveChange,
    onIncludeDoNotContactChange,
    onChange,
    onClear,
    onRefresh,
}: {
    readonly searchControl?: ReactNode;
    readonly mapControls?: ReactNode;
    readonly filters: CoverageFilters;
    readonly facetOptions: FacetOptions;
    readonly dayLabels: ReadonlyArray<string>;
    readonly filtersActive: boolean;
    readonly clearEpoch: number;
    readonly includeInactive: boolean;
    readonly includeDoNotContact: boolean;
    readonly onIncludeInactiveChange: (includeInactive: boolean) => void;
    readonly onIncludeDoNotContactChange: (value: boolean) => void;
    readonly onChange: (update: CoverageSearchUpdate) => void;
    readonly onClear: () => void;
    readonly onRefresh?: () => void;
}) {
    const [searchDraft, setSearchDraft] = useState(filters.search);
    const debouncedSearch = useDebouncedValue(searchDraft, SEARCH_DEBOUNCE_MS);
    const searchEditedRef = useRef(false);
    const lastPushedSearchRef = useRef(filters.search);

    useEffect(() => {
        // A URL search value we did not push ourselves (coverage
        // click-through, shared link) is authoritative: adopt it and drop
        // any pending local edit so the debounce can't rewrite it.
        if (filters.search !== lastPushedSearchRef.current) {
            lastPushedSearchRef.current = filters.search;
            searchEditedRef.current = false;
            setSearchDraft(filters.search);
        }
    }, [filters.search]);

    useEffect(() => {
        if (clearEpoch === 0) {
            return;
        }
        // Every clear (filter bar button, empty state, future callers)
        // bumps the epoch; drop the draft even when the URL search value
        // was already empty and the adopt-effect above cannot fire.
        lastPushedSearchRef.current = '';
        searchEditedRef.current = false;
        setSearchDraft('');
    }, [clearEpoch]);

    useEffect(() => {
        if (!searchEditedRef.current) {
            return;
        }
        searchEditedRef.current = false;
        lastPushedSearchRef.current = debouncedSearch;
        onChange({ search: debouncedSearch });
    }, [debouncedSearch, onChange]);

    const filterCategories: ReadonlyArray<IncludeOnlyFilterMenuCategory> = [
        {
            id: 'offices',
            title: 'Office',
            options: facetOptions.offices.map(({ key, label }) => ({
                value: key,
                label,
            })),
            values: [...filters.offices],
            onChange: (offices: Array<string>) => onChange({ offices }),
        },
        {
            id: 'cities',
            title: 'City',
            options: facetOptions.cities.map(({ key, label }) => ({
                value: key,
                label,
            })),
            values: [...filters.cities],
            onChange: (cities: Array<string>) => onChange({ cities }),
        },
        {
            id: 'zips',
            title: 'ZIP',
            options: facetOptions.zips.map(({ key, label }) => ({
                value: key,
                label,
            })),
            values: [...filters.zips],
            onChange: (zips: Array<string>) => onChange({ zips }),
        },
        {
            id: 'days',
            title: 'Day',
            options: dayLabels.map((label, index) => ({
                value: String(index),
                label: label || `Day ${index + 1}`,
            })),
            values: filters.days.map(String),
            onChange: (days: Array<string>) =>
                onChange({ days: days.map(Number).sort((a, b) => a - b) }),
        },
        {
            id: 'dayParts',
            title: 'Time of day',
            options: DAY_PARTS.map((part) => ({
                value: part.key,
                label: `${part.label} · ${part.timeLabel}`,
            })),
            values: [...filters.dayParts],
            onChange: (dayParts: Array<string>) =>
                onChange({ dayParts: dayParts as Array<DayPartKey> }),
        },
        {
            id: 'tags',
            title: 'Tags',
            options: facetOptions.tags.map((value) => ({
                value,
                label: value,
            })),
            values: [...filters.tags],
            onChange: (tags: Array<string>) => onChange({ tags }),
        },
        {
            id: 'employmentTypes',
            title: 'Employment',
            options: facetOptions.employmentTypes.map((value) => ({
                value,
                label: EMPLOYMENT_TYPE_LABELS[value] ?? value,
            })),
            values: [...filters.employmentTypes],
            onChange: (employmentTypes: Array<string>) =>
                onChange({ employmentTypes }),
        },
    ].map(({ values, onChange: onValuesChange, ...category }) => ({
        ...category,
        selectionMode: 'include-only',
        selectedValues: new Set(values),
        onToggle: (value) =>
            onValuesChange(
                values.includes(value)
                    ? values.filter((selected) => selected !== value)
                    : [...values, value],
            ),
        onClear: () => onValuesChange([]),
    }));
    const activeFilterCount = filterCategories.reduce(
        (count, category) => count + category.selectedValues.size,
        0,
    );

    return (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            {searchControl ?? (
                <SearchInput
                    value={searchDraft}
                    onValueChange={(value) => {
                        searchEditedRef.current = true;
                        setSearchDraft(value);
                    }}
                    placeholder="Search caregivers"
                    aria-label="Search caregivers by name"
                    containerClassName="w-52"
                />
            )}
            <div className="flex items-center">
                <IncludeExcludeFilterMenu
                    categories={filterCategories}
                    onClearAll={onClear}
                />
                <span className="sr-only" role="status">
                    {activeFilterCount} active filters in the filter menu
                </span>
            </div>
            {onRefresh ? (
                <Button variant="outline" onClick={onRefresh}>
                    <RefreshCw className="size-4" aria-hidden={true} />
                    Refresh
                </Button>
            ) : null}
            {filters.days.length > 0 && filters.matchMode === 'any' ? (
                <span className="text-muted-foreground bg-muted rounded-full px-2.5 py-0.5 text-xs">
                    Matching any selected day
                </span>
            ) : null}
            {filtersActive ? (
                <Button variant="ghost" size="sm" onClick={onClear}>
                    Clear filters
                </Button>
            ) : null}
            {mapControls}
            <label className="text-muted-foreground flex cursor-pointer items-center gap-1.5 text-sm">
                <Checkbox
                    checked={includeInactive}
                    onCheckedChange={(checked) =>
                        onIncludeInactiveChange(checked === true)
                    }
                />
                Include inactive caregivers
            </label>
            <label className="text-muted-foreground flex cursor-pointer items-center gap-1.5 text-sm">
                <Checkbox
                    checked={includeDoNotContact}
                    onCheckedChange={(checked) =>
                        onIncludeDoNotContactChange(checked === true)
                    }
                />
                Include caregivers on the skip list
            </label>
        </div>
    );
}
